import { Language } from '@/context/LanguageContext';
import { citySpecialties } from '@/data/citySpecialties';
import { supabase } from '@/integrations/supabase/client';

interface SitemapEntry {
  url: string;
  lastmod: string;
  changefreq: string;
  priority: string;
  language?: Language;
}

/**
 * Fetch all active cities from Supabase database
 */
const fetchAllCitiesFromDatabase = async () => {
  const { data: cities, error } = await supabase
    .from('cities')
    .select('name, slug')
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching cities:', error);
    return [];
  }

  return cities || [];
};

/**
 * Generate sitemap entries for city specialty pages
 * For ALL cities (32,853) × 26 specialties = 850,694 URLs (English only)
 */
export const generateSpecialtySitemapEntries = async (
  baseUrl: string
): Promise<SitemapEntry[]> => {
  const currentDate = new Date().toISOString().split('T')[0];
  const entries: SitemapEntry[] = [];
  
  // Fetch ALL cities from database
  const cities = await fetchAllCitiesFromDatabase();
  
  console.log(`🏙️ Generating specialty sitemaps for ${cities.length} cities`);
  console.log(`📊 Total specialty pages: ${cities.length * citySpecialties.length}`);

  // Generate entries for each city-specialty combination (English only)
  cities.forEach(city => {
    citySpecialties.forEach(specialty => {
      const specialtySlug = specialty.slugs.en;
      const url = `${baseUrl}/city/${city.slug}/${specialtySlug}`;
      
      entries.push({
        url,
        lastmod: currentDate,
        changefreq: 'weekly',
        priority: '0.7',
        language: 'en'
      });
    });
  });

  console.log(`✅ Generated ${entries.length.toLocaleString()} specialty URLs`);
  return entries;
};

/**
 * Get statistics about specialty sitemap generation
 */
export const getSpecialtySitemapStats = async () => {
  const { count } = await supabase
    .from('cities')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true);

  const totalCities = count || 0;
  
  return {
    totalCities,
    totalSpecialties: citySpecialties.length,
    totalUrls: totalCities * citySpecialties.length
  };
};
